import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "source",
    "field",
    "fieldContainer",
    "dynamic",
    "dynamicContainer",
    "hiddenValue",
  ];

  connect() {
    // Load field options from data attributes
    this.fieldOptions = {
      ticket: this.parseDataAttribute("ticketFields"),
      ticket_custom_data: this.parseDataAttribute("customFields"),
      order: this.parseDataAttribute("orderFields"),
      order_customer: this.parseDataAttribute("customerFields"),
      order_custom_data: this.parseDataAttribute("customFields"),
    };

    // Parse initial value if present
    const initialValue = this.element.dataset.initialValue;
    if (initialValue) {
      this.parseInitialValue(initialValue);
    }
  }

  parseDataAttribute(attributeName) {
    const data = this.element.dataset[attributeName];
    if (!data) return [];

    try {
      const parsed = JSON.parse(data);
      // If it's custom fields, map to expected format
      if (attributeName === "customFields") {
        return parsed.map((f) => ({
          value: f.slug,
          label: f.name,
        }));
      }
      // Otherwise it's already in the correct format
      return parsed;
    } catch (e) {
      console.error(`Failed to parse ${attributeName}:`, e);
      return [];
    }
  }

  parseInitialValue(value) {
    // Try to parse the Liquid template to determine source and field
    // Patterns:
    // {{ ticket.field_name }} -> ticket source
    // {{ ticket.custom_data.field_name }} -> ticket_custom_data source
    // {{ order.field_name }} -> order source
    // {{ order.customer.field_name }} -> order_customer source
    // {{ order.custom_data.field_name }} -> order_custom_data source
    // anything else -> dynamic

    const ticketMatch = value.match(/^\{\{\s*ticket\.(\w+)\s*\}\}$/);
    const ticketCustomMatch = value.match(
      /^\{\{\s*ticket\.custom_data\.(\w+)\s*\}\}$/,
    );
    const orderMatch = value.match(/^\{\{\s*order\.(\w+)\s*\}\}$/);
    const orderCustomerMatch = value.match(
      /^\{\{\s*order\.customer\.(\w+)\s*\}\}$/,
    );
    const orderCustomMatch = value.match(
      /^\{\{\s*order\.custom_data\.(\w+)\s*\}\}$/,
    );

    if (ticketMatch) {
      this.sourceTarget.value = "ticket";
      this.updateFieldOptions("ticket");
      this.fieldTarget.value = ticketMatch[1];
      this.hiddenValueTarget.value = value;
    } else if (ticketCustomMatch) {
      this.sourceTarget.value = "ticket_custom_data";
      this.updateFieldOptions("ticket_custom_data");
      this.fieldTarget.value = ticketCustomMatch[1];
      this.hiddenValueTarget.value = value;
    } else if (orderCustomerMatch) {
      this.sourceTarget.value = "order_customer";
      this.updateFieldOptions("order_customer");
      this.fieldTarget.value = orderCustomerMatch[1];
      this.hiddenValueTarget.value = value;
    } else if (orderMatch) {
      this.sourceTarget.value = "order";
      this.updateFieldOptions("order");
      this.fieldTarget.value = orderMatch[1];
      this.hiddenValueTarget.value = value;
    } else if (orderCustomMatch) {
      this.sourceTarget.value = "order_custom_data";
      this.updateFieldOptions("order_custom_data");
      this.fieldTarget.value = orderCustomMatch[1];
      this.hiddenValueTarget.value = value;
    } else if (value) {
      // Dynamic/complex template
      this.sourceTarget.value = "dynamic";
      this.showDynamic();
      this.dynamicTarget.value = value;
      this.hiddenValueTarget.value = value;
    }
  }

  sourceChanged() {
    const source = this.sourceTarget.value;

    if (source === "dynamic") {
      this.showDynamic();
    } else {
      this.showField();
      this.updateFieldOptions(source);
    }

    this.updateHiddenValue();
  }

  fieldChanged() {
    this.updateHiddenValue();
  }

  dynamicChanged() {
    this.hiddenValueTarget.value = this.dynamicTarget.value;
  }

  showField() {
    this.fieldContainerTarget.style.display = "";
    this.dynamicContainerTarget.style.display = "none";
  }

  showDynamic() {
    this.fieldContainerTarget.style.display = "none";
    this.dynamicContainerTarget.style.display = "";
  }

  updateFieldOptions(source) {
    const options = this.fieldOptions[source] || [];
    const currentValue = this.fieldTarget.value;

    // Clear existing options
    this.fieldTarget.innerHTML = '<option value="">Select field</option>';

    // Add new options
    options.forEach((option) => {
      const optionElement = document.createElement("option");
      optionElement.value = option.value;
      optionElement.textContent = option.label;
      if (option.value === currentValue) {
        optionElement.selected = true;
      }
      this.fieldTarget.appendChild(optionElement);
    });
  }

  updateHiddenValue() {
    const source = this.sourceTarget.value;
    const field = this.fieldTarget.value;

    if (!source || !field) {
      this.hiddenValueTarget.value = "";
      return;
    }

    // Construct Liquid template based on source and field
    let template = "";
    switch (source) {
      case "ticket":
        template = `{{ ticket.${field} }}`;
        break;
      case "ticket_custom_data":
        template = `{{ ticket.custom_data.${field} }}`;
        break;
      case "order":
        template = `{{ order.${field} }}`;
        break;
      case "order_customer":
        template = `{{ order.customer.${field} }}`;
        break;
      case "order_custom_data":
        template = `{{ order.custom_data.${field} }}`;
        break;
    }

    this.hiddenValueTarget.value = template;
  }
}
